// NOTE (deprecation): The `getLocale*` helpers below are deprecated as of
// Angular 16+ — Angular recommends the native `Intl` API (`Intl.DateTimeFormat`,
// `Intl.NumberFormat`, `Intl.PluralRules`) instead. We knowingly keep them here
// because they conveniently yield the CLDR *pattern strings* (e.g. 'EEEE d MMMM y')
// that AngularJS's $locale.DATETIME_FORMATS requires; `Intl` only formats values
// and would need those patterns reconstructed via `formatToParts`.
// They still work in our current Angular version and the locale data is registered
// by the i18n (localize) build. If a future Angular major REMOVES these getters
// before AngularJS is fully retired, this bridge must be rewritten on top of the
// `Intl` API (reconstructing patterns from `Intl.DateTimeFormat(...).formatToParts`).
// The public surface (`window.kzAngularLocaleData` consumed by
// src/app/core/locale.config.js) can stay unchanged across that rewrite.
import {
  FormStyle,
  FormatWidth,
  NumberFormatStyle,
  NumberSymbol,
  Plural,
  TranslationWidth,
  getLocaleCurrencySymbol,
  getLocaleDateFormat,
  getLocaleDateTimeFormat,
  getLocaleDayNames,
  getLocaleDayPeriods,
  getLocaleEraNames,
  getLocaleFirstDayOfWeek,
  getLocaleMonthNames,
  getLocaleNumberFormat,
  getLocaleNumberSymbol,
  getLocalePluralCase,
  getLocaleTimeFormat,
  getLocaleWeekEndRange,
} from '@angular/common'

const PLURAL_CATEGORIES: Record<Plural, string> = {
  [Plural.Zero]: 'zero',
  [Plural.One]: 'one',
  [Plural.Two]: 'two',
  [Plural.Few]: 'few',
  [Plural.Many]: 'many',
  [Plural.Other]: 'other',
}

// Translate a single CLDR number sub-pattern (e.g. "#,##0.###" or "#,##0.00 ¤")
// into the shape AngularJS's $locale.NUMBER_FORMATS.PATTERNS expects.
function parsePositivePattern(pattern: string) {
  const core = pattern.match(/[0#.,]+/)
  if (!core) {
    return { minInt: 1, minFrac: 0, maxFrac: 0, gSize: 0, lgSize: 0, posPre: pattern, posSuf: '' }
  }
  const start = core.index ?? 0
  const posPre = pattern.slice(0, start)
  const posSuf = pattern.slice(start + core[0].length)

  const [intPart, fracPart = ''] = core[0].split('.')
  const minInt = Math.max((intPart.match(/0/g) || []).length, 1)
  const minFrac = (fracPart.match(/0/g) || []).length
  const maxFrac = (fracPart.match(/[0#]/g) || []).length

  let gSize = 0
  let lgSize = 0
  const firstComma = intPart.indexOf(',')
  const lastComma = intPart.lastIndexOf(',')
  if (lastComma !== -1) {
    gSize = intPart.length - 1 - lastComma
    lgSize = firstComma !== lastComma ? lastComma - firstComma - 1 : gSize
  }

  return { minInt, minFrac, maxFrac, gSize, lgSize, posPre, posSuf }
}

function parseNumberPattern(pattern: string) {
  const [positive, negative] = pattern.split(';')
  const p = parsePositivePattern(positive)
  let negPre: string
  let negSuf: string
  if (negative) {
    const n = parsePositivePattern(negative)
    negPre = n.posPre
    negSuf = n.posSuf
  } else {
    negPre = '-' + p.posPre
    negSuf = p.posSuf
  }
  return { ...p, negPre, negSuf }
}

/**
 * Build an AngularJS `$locale`-shaped object (DATETIME_FORMATS / NUMBER_FORMATS /
 * pluralCat) from Angular's own registered locale data for `localeId`.
 *
 * This lets the upgraded AngularJS layer mirror exactly the locale Angular was
 * compiled with (LOCALE_ID / $localize), so date and number filters render in the
 * same language/format as the Angular app for every locale we ship (fr-CA, en-AU,
 * en-CA, en-GB, …) without any hand-maintained locale tables.
 *
 * Returns `null` if Angular has no data registered for the locale, in which case
 * the legacy layer simply keeps AngularJS's built-in default `$locale`.
 */
export function buildAngularJsLocaleData(localeId: string): object | null {
  try {
    const mediumDate = getLocaleDateFormat(localeId, FormatWidth.Medium)
    const shortDate = getLocaleDateFormat(localeId, FormatWidth.Short)
    const mediumTime = getLocaleTimeFormat(localeId, FormatWidth.Medium)
    const shortTime = getLocaleTimeFormat(localeId, FormatWidth.Short)
    const dtMedium = getLocaleDateTimeFormat(localeId, FormatWidth.Medium)
    const dtShort = getLocaleDateTimeFormat(localeId, FormatWidth.Short)

    const compose = (template: string, date: string, time: string) =>
      template.replace('{1}', date).replace('{0}', time)

    const pluralCase = getLocalePluralCase(localeId)

    return {
      DATETIME_FORMATS: {
        AMPMS: getLocaleDayPeriods(localeId, FormStyle.Format, TranslationWidth.Abbreviated),
        DAY: getLocaleDayNames(localeId, FormStyle.Format, TranslationWidth.Wide),
        ERANAMES: getLocaleEraNames(localeId, TranslationWidth.Wide),
        ERAS: getLocaleEraNames(localeId, TranslationWidth.Abbreviated),
        FIRSTDAYOFWEEK: getLocaleFirstDayOfWeek(localeId),
        MONTH: getLocaleMonthNames(localeId, FormStyle.Format, TranslationWidth.Wide),
        SHORTDAY: getLocaleDayNames(localeId, FormStyle.Format, TranslationWidth.Abbreviated),
        SHORTMONTH: getLocaleMonthNames(localeId, FormStyle.Format, TranslationWidth.Abbreviated),
        STANDALONEMONTH: getLocaleMonthNames(localeId, FormStyle.Standalone, TranslationWidth.Wide),
        WEEKENDRANGE: getLocaleWeekEndRange(localeId),
        fullDate: getLocaleDateFormat(localeId, FormatWidth.Full),
        longDate: getLocaleDateFormat(localeId, FormatWidth.Long),
        medium: compose(dtMedium, mediumDate, mediumTime),
        mediumDate,
        mediumTime,
        short: compose(dtShort, shortDate, shortTime),
        shortDate,
        shortTime,
      },
      NUMBER_FORMATS: {
        CURRENCY_SYM: getLocaleCurrencySymbol(localeId) || '',
        DECIMAL_SEP: getLocaleNumberSymbol(localeId, NumberSymbol.Decimal),
        GROUP_SEP: getLocaleNumberSymbol(localeId, NumberSymbol.Group),
        PATTERNS: [
          parseNumberPattern(getLocaleNumberFormat(localeId, NumberFormatStyle.Decimal)),
          parseNumberPattern(getLocaleNumberFormat(localeId, NumberFormatStyle.Currency)),
        ],
      },
      id: localeId.toLowerCase(),
      localeID: localeId.replace('-', '_'),
      pluralCat: (n: number) => PLURAL_CATEGORIES[pluralCase(n)] || 'other',
    }
  } catch {
    return null
  }
}
